import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui-kit/table';
import { LeaveOverviewCategoryBreakdown } from '../../types/leave-overview.types';

/** Allocated/Taken/Requested/Remaining per leave category of one employee (Leave Overview data). An
 * "Overtime Leave" has nothing allocated: its remaining is the overtime balance in days. */
export const LeaveCategoryBreakdownTable = ({
  categories,
}: {
  categories: LeaveOverviewCategoryBreakdown[];
}) => {
  const { t } = useTranslation();
  const allocatedOf = (category: LeaveOverviewCategoryBreakdown) =>
    category.IsUnlimited ? t('UNLIMITED') : category.IsOvertimeLeave ? '-' : category.Allocated;
  const remainingOf = (category: LeaveOverviewCategoryBreakdown) =>
    category.IsUnlimited ? t('UNLIMITED') : category.Remaining;

  return (
    <>
      {/* On a phone each category is a card (as the data tables' cards): its values two per line. */}
      <div className="flex flex-col gap-3 md:hidden">
        {categories.map((category) => (
          <div
            key={category.LeaveCategoryId}
            className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4"
          >
            <div className="break-words text-base font-semibold">{category.LeaveCategory}</div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
              {[
                { label: 'ALLOCATED_LEAVE', value: allocatedOf(category) },
                { label: 'TAKEN_LEAVES', value: category.Taken },
                { label: 'REQUESTED_LEAVES', value: category.Requested },
                { label: 'REMAINING_LEAVE', value: remainingOf(category) },
              ].map((pair) => (
                <div key={pair.label} className="flex min-w-0 flex-col gap-0.5">
                  <dt className="text-xs text-muted-foreground">{t(pair.label)}</dt>
                  <dd className="break-words text-sm font-medium tabular-nums">{pair.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>

      {/* A fixed layout always fits its container, so it never scrolls sideways: the category takes
          a third, the four numbers share the rest; a long title wraps to a second line instead. */}
      <Table
        divClassname="max-md:hidden"
        className="table-fixed [&_td]:px-2 [&_th]:h-auto [&_th]:px-2 [&_th]:py-2 [&_th]:leading-tight"
      >
        <TableHeader>
          <TableRow>
            <TableHead className="w-1/3">{t('CATEGORY')}</TableHead>
            <TableHead className="text-right">{t('ALLOCATED_LEAVE')}</TableHead>
            <TableHead className="text-right">{t('TAKEN_LEAVES')}</TableHead>
            <TableHead className="text-right">{t('REQUESTED_LEAVES')}</TableHead>
            <TableHead className="text-right">{t('REMAINING_LEAVE')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {categories.map((category) => (
            <TableRow key={category.LeaveCategoryId}>
              <TableCell className="break-words font-medium">{category.LeaveCategory}</TableCell>
              <TableCell className="text-right tabular-nums">{allocatedOf(category)}</TableCell>
              <TableCell className="text-right tabular-nums">{category.Taken}</TableCell>
              <TableCell className="text-right tabular-nums">{category.Requested}</TableCell>
              <TableCell className="text-right tabular-nums">{remainingOf(category)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
};
